import { createContext, useContext, type ReactNode } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Button, Card } from "@/deps/ui-library";
import { useApprovals } from "@/api/queries";
import type { ApprovalItem } from "@/api/types";
import { useToast } from "@/app/toasts";

export type ReviewFilter = "all" | "Verify" | "Approve";
type ReviewAction = "verify" | "approve" | "return";

/** Where a document in the queue lives. A bill in the queue used to open as an invoice and fail to load. */
export function documentPath(item: Pick<ApprovalItem, "kind" | "documentId">): string {
  const id = item.documentId;
  switch (item.kind) {
    case "bill":
      return `/purchases/bills/${id}`;
    case "debitNote":
      return `/purchases/debit-notes/${id}`;
    case "fundTransfer":
      return `/banking/transfers/${id}`;
    case "payment":
      return `/purchases/payments/${id}`;
    case "employeePayment":
      return `/accounting/employees/movements/${id}`;
    case "yearClose":
      return "/accounting/year-end";
    case "purchaseOrder":
      return `/purchases/orders/${id}`;
    case "journal":
      return `/accounting/journals/${id}`;
    case "creditNote":
      return `/sales/credit-notes/${id}`;
    default:
      return `/sales/invoices/${id}`;
  }
}

/** The search that keeps a document in the sequence, as the address of a real link. */
export const reviewHref = (item: ApprovalItem, filter: ReviewFilter) =>
  `${documentPath(item)}?review=${filter}`;

/** What a queued document is called. Most are drafts with no number until they're issued. */
export const KIND_LABEL: Record<ApprovalItem["kind"], string> = {
  invoice: "Invoice",
  creditNote: "Credit note",
  receipt: "Receipt",
  bill: "Bill",
  debitNote: "Debit note",
  payment: "Payment",
  employeePayment: "Employee payment",
  journal: "Journal",
  fundTransfer: "Fund transfer",
  purchaseOrder: "Purchase order",
  yearClose: "Year-end close",
};

const DONE: Record<ReviewAction, string> = {
  verify: "verified",
  approve: "approved",
  return: "returned",
};

/** Tells the sequence a step was taken. Null when the document wasn't opened from the queue. */
const ReviewContext = createContext<((action: ReviewAction) => void) | null>(null);

export const useApprovalReview = () => useContext(ReviewContext);

/**
 * Reviewing the queue in sequence: each document opens on its own page, where its own Verify, Approve
 * and Return already are, and this bar holds the place. After a step the next document loads, so the
 * approver never goes back to the list between actions.
 *
 * The queue comes from the API rather than from the browser, so a document opened in a new tab knows
 * its place too. Moving on happens only when the step succeeded: a refusal has to stay on the page
 * that explains it.
 */
export function ApprovalReview({ children }: { children: ReactNode }) {
  const { pathname, searchStr } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const filter = new URLSearchParams(searchStr).get("review");
  const reviewing = filter !== null;
  const { data } = useApprovals(reviewing);

  const items = (data ?? []).filter(
    (item) => (filter !== "Verify" && filter !== "Approve") || item.step === filter,
  );
  const index = items.findIndex((item) => documentPath(item) === pathname);

  const open = (item: ApprovalItem) =>
    void navigate({
      to: documentPath(item) as never,
      search: { review: filter } as never,
    });
  const backToList = () => void navigate({ to: "/accounting/approvals" });

  const acted = (action: ReviewAction) => {
    const current = items[index];
    const rest = items.filter((_, position) => position !== index);
    // What was after this one now sits at its position, and the last one wraps to the first.
    const next = rest[index] ?? rest[0];
    toast({ title: `${current ? KIND_LABEL[current.kind] : "Document"} ${DONE[action]}` });
    if (next) open(next);
    else backToList();
  };

  const count = items.length;
  const place = (offset: number) => items[(index + offset + count) % count];

  return (
    <ReviewContext.Provider value={reviewing ? acted : null}>
      {reviewing && data ? (
        <Card
          size="none"
          className="no-print mb-4 flex flex-wrap items-center gap-2 px-3 py-2"
          aria-label="Reviewing approvals"
        >
          <span className="text-fg text-sm font-medium">Approvals</span>
          <span className="text-fg-muted text-xs">
            {index >= 0 ? `${index + 1} of ${count}` : "This one isn't waiting on you any more"}
          </span>
          <span className="flex-1" />
          {index >= 0 && count > 1 ? (
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              leftIcon="ChevronLeft"
              onClick={() => open(place(-1)!)}
            >
              Previous
            </Button>
          ) : null}
          {(index >= 0 && count > 1) || (index < 0 && count > 0) ? (
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              rightIcon="ChevronRight"
              onClick={() => open(index >= 0 ? place(1)! : items[0]!)}
            >
              Next
            </Button>
          ) : null}
          <Button
            colorScheme="default"
            variant="ghost"
            size="sm"
            leftIcon="List"
            onClick={backToList}
          >
            Back to list
          </Button>
        </Card>
      ) : null}
      {children}
    </ReviewContext.Provider>
  );
}
